import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import {
    AuthenticatedEvent,
    WebSocketConfig,
    WebSocketEvent,
    WebSocketStatus
} from '../interfaces/websocket/websocket-events.interface';
import { AuthService } from '../services/auth.service';

/**
 * Base WebSocket Service
 * Handles Socket.IO connection, authentication, and event management
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private readonly authService = inject(AuthService);

  private socket: Socket | null = null;
  private readonly destroy$ = new Subject<void>();

  // Connection status
  private readonly status$ = new BehaviorSubject<WebSocketStatus>(WebSocketStatus.DISCONNECTED);
  private readonly connectionError$ = new Subject<string>();

  // Events
  private readonly events$ = new Subject<WebSocketEvent>();
  private readonly authenticatedUser$ = new BehaviorSubject<AuthenticatedEvent | null>(null);

  // Default configuration
  private readonly defaultConfig: WebSocketConfig = {
    url: environment.socketUrl || '',
    namespace: '/ws',
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    timeout: 20000,
    forceNew: true
  };

  constructor() {
    // Initialize connection when auth token is available
    this.authService.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated && this.status$.value === WebSocketStatus.DISCONNECTED) {
        this.connect();
      } else if (!isAuthenticated && this.socket) {
        // Was: `else if (!isAuthenticated && this.isConnected())`.
        // isConnected() reads socket.connected, which is false while a
        // (re)connect attempt is in flight - so logging out mid-reconnect
        // never called disconnect() here. The socket.io client kept its own
        // internal reconnection loop running with the now-invalidated
        // token, and status$ was never reset to DISCONNECTED, which then
        // also blocked the `isAuthenticated && status$ === DISCONNECTED`
        // branch above from reconnecting on the next login - only a full
        // page refresh (which recreates this singleton) recovered.
        this.disconnect();
      }
    });
  }

  /**
   * Connect to WebSocket server
   */
  connect(config?: Partial<WebSocketConfig>): void {
    if (this.socket && this.isConnected()) {
      return;
    }

    // Clean up any stale socket before creating new one
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    const token = this.authService.getAccessToken();
    if (!token) {
      this.connectionError$.next('Authentication token required');
      return;
    }

    const finalConfig = { ...this.defaultConfig, ...config };
    this.status$.next(WebSocketStatus.CONNECTING);

    try {
      // When url is empty, connect to current origin (proxy handles routing)
      const wsUrl = finalConfig.url
        ? `${finalConfig.url}${finalConfig.namespace}`
        : finalConfig.namespace || '/ws';

      this.socket = io(wsUrl, {
        query: {
          token: token // Backend expects token in query.token
        },
        transports: ['websocket', 'polling'],
        forceNew: finalConfig.forceNew,
        reconnection: finalConfig.reconnection,
        reconnectionAttempts: finalConfig.reconnectionAttempts,
        reconnectionDelay: finalConfig.reconnectionDelay,
        reconnectionDelayMax: 60000,
        timeout: finalConfig.timeout
      });

      this.setupEventListeners();

    } catch (error) {
      this.status$.next(WebSocketStatus.ERROR);
      this.connectionError$.next(`Connection failed: ${error}`);
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.status$.next(WebSocketStatus.DISCONNECTED);
    this.authenticatedUser$.next(null);
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get connection status observable
   */
  getStatus$(): Observable<WebSocketStatus> {
    return this.status$.asObservable();
  }

  /**
   * Get connection errors observable
   */
  getConnectionError$(): Observable<string> {
    return this.connectionError$.asObservable();
  }

  /**
   * Get authenticated user data
   */
  getAuthenticatedUser$(): Observable<AuthenticatedEvent | null> {
    return this.authenticatedUser$.asObservable();
  }

  /**
   * Get all WebSocket events
   */
  getEvents$(): Observable<WebSocketEvent> {
    return this.events$.asObservable();
  }

  /**
   * Get specific event type
   */
  getEventsByType$<T extends WebSocketEvent['type']>(
    eventType: T
  ): Observable<unknown> {
    return this.events$.pipe(
      filter(event => event.type === eventType),
      map(event => event.data)
    );
  }

  /**
   * Emit event to server
   */
  emit(event: string, data?: unknown): void {
    if (!this.socket || !this.isConnected()) {
      return;
    }

    this.socket.emit(event, data);
  }

  /**
   * Join a specific channel/room for table updates
   */
  joinChannel(tableName: string): void {
    const channelName = `table-${tableName}`;
    this.emit('join-channel', { channel: channelName });
  }

  /**
   * Leave a specific channel/room for table updates
   */
  leaveChannel(tableName: string): void {
    const channelName = `table-${tableName}`;
    this.emit('leave-channel', { channel: channelName });
  }

  /**
   * Test method to simulate user creation
   */
  testUserCreate(): void {

    if (!this.socket?.connected) {
      return;
    }

    this.emit('test-user-create', {});
  }

  /**
   * Setup Socket.IO event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) {
      return;
    }


    // Connection events
    this.socket.on('connect', () => {
      this.status$.next(WebSocketStatus.CONNECTED);
    });

    this.socket.on('disconnect', (reason: string) => {
      this.status$.next(WebSocketStatus.DISCONNECTED);
      this.authenticatedUser$.next(null);
      this.events$.next({ type: 'disconnect', data: { socketId: this.socket?.id || '', timestamp: new Date().toISOString(), reason } });
    });

    this.socket.on('connect_error', (error: Error) => {
      this.status$.next(WebSocketStatus.ERROR);
      this.connectionError$.next(error?.message);
    });

    // Authentication event
    this.socket.on('authenticated', (data: AuthenticatedEvent) => {
      this.authenticatedUser$.next(data);
      this.events$.next({ type: 'authenticated', data });
    });

    // User events
    this.socket.on('user:created', (data) => {
      this.events$.next({ type: 'user:created', data });
    });

    this.socket.on('user:updated', (data) => {
      this.events$.next({ type: 'user:updated', data });
    });

    this.socket.on('user:deleted', (data) => {
      this.events$.next({ type: 'user:deleted', data });
    });

    // User Settings events
    this.socket.on('user:setting:updated', (data) => {
      this.events$.next({ type: 'user:setting:updated', data });
    });

    // User Devices events
    this.socket.on('user:device:registered', (data) => {
      this.events$.next({ type: 'user:device:registered', data });
    });

    this.socket.on('user:device:deactivated', (data) => {
      this.events$.next({ type: 'user:device:deactivated', data });
    });

    // User Ratings events
    this.socket.on('user:rating:created', (data) => {
      this.events$.next({ type: 'user:rating:created', data });
    });

    // Notification events
    this.socket.on('notification', (data) => {
      this.events$.next({ type: 'notification', data });
    });

    // Chat events
    this.socket.on('chat:message', (data) => {
      this.events$.next({ type: 'chat:message', data });
    });

    this.socket.on('chat:typing', (data) => {
      this.events$.next({ type: 'chat:typing', data });
    });

    // System events
    this.socket.on('system:alert', (data) => {
      this.events$.next({ type: 'system:alert', data });
    });

    // Reconnection events
    this.socket.on('reconnect', (attempt: number) => {
      this.status$.next(WebSocketStatus.CONNECTED);
    });

    this.socket.on('reconnect_attempt', (attempt: number) => {
      this.status$.next(WebSocketStatus.RECONNECTING);
    });

    this.socket.on('reconnect_error', (error: Error) => {
      this.connectionError$.next(`Reconnection failed: ${error?.message}`);
    });

    this.socket.on('reconnect_failed', () => {
      this.status$.next(WebSocketStatus.ERROR);
      this.connectionError$.next('Reconnection failed after maximum attempts');
      // After built-in reconnect gives up, schedule a fresh connection attempt
      setTimeout(() => {
        if (this.authService.getAccessToken()) {
          this.disconnect();
          this.connect();
        }
      }, 30000);
    });
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }
}
